/**
 * Permission types for PBAC (Permission-Based Access Control)
 * These permissions are used throughout the application to control access to features and actions.
 *
 * Source: Backend GraphQL API - userPermissionsForAccount query
 */
import { UserRole } from './roleUtils';

// Account Users permissions
export type AccountUsersPermission =
  | 'accountusers-create'
  | 'accountusers-read'
  | 'accountusers-update';

// Campaigns permissions
export type CampaignsPermission =
  | 'campaigns-read'
  | 'campaigns-create'
  | 'campaigns-update'
  | 'campaigns-delete';

// Creatives permissions
export type CreativesPermission =
  | 'creatives-read'
  | 'creatives-create'
  | 'creatives-update'
  | 'creatives-delete';

// Audience permissions
export type AudiencePermission =
  | 'audience-read'
  | 'audience-create'
  | 'audience-update'
  | 'audience-delete';

// Data permissions
export type DataPermission =
  | 'data-read'
  | 'data-create'
  | 'data-update'
  | 'data-delete';

// Analytics permissions
export type AnalyticsPermission =
  | 'analytics-read'
  | 'analytics-create'
  | 'analytics-update'
  | 'analytics-delete';

// Brand Kit permissions
export type BrandKitPermission =
  | 'brandkit-read'
  | 'brandkit-create'
  | 'brandkit-update';

// Settings permissions
export type SettingsPermission =
  | 'settings-read'
  | 'settings-create'
  | 'settings-update';

// Notification permissions
export type NotificationPermission = 'notifications-configure';

/**
 * Union type of all available permissions in the system
 */
export type Permission =
  | AccountUsersPermission
  | CampaignsPermission
  | CreativesPermission
  | AudiencePermission
  | DataPermission
  | AnalyticsPermission
  | BrandKitPermission
  | SettingsPermission
  | NotificationPermission;

/**
 * Permission constants for easy reference and type-safe usage
 */
export const PERMISSIONS = {
  // Account Users
  ACCOUNTUSERS_CREATE: 'accountusers-create' as const,
  ACCOUNTUSERS_READ: 'accountusers-read' as const,
  ACCOUNTUSERS_UPDATE: 'accountusers-update' as const,

  // Campaigns
  CAMPAIGNS_READ: 'campaigns-read' as const,
  CAMPAIGNS_CREATE: 'campaigns-create' as const,
  CAMPAIGNS_UPDATE: 'campaigns-update' as const,
  CAMPAIGNS_DELETE: 'campaigns-delete' as const,

  // Creatives
  CREATIVES_READ: 'creatives-read' as const,
  CREATIVES_CREATE: 'creatives-create' as const,
  CREATIVES_UPDATE: 'creatives-update' as const,
  CREATIVES_DELETE: 'creatives-delete' as const,

  // Audience
  AUDIENCE_READ: 'audience-read' as const,
  AUDIENCE_CREATE: 'audience-create' as const,
  AUDIENCE_UPDATE: 'audience-update' as const,
  AUDIENCE_DELETE: 'audience-delete' as const,

  // Data
  DATA_READ: 'data-read' as const,
  DATA_CREATE: 'data-create' as const,
  DATA_UPDATE: 'data-update' as const,
  DATA_DELETE: 'data-delete' as const,

  // Analytics
  ANALYTICS_READ: 'analytics-read' as const,
  ANALYTICS_CREATE: 'analytics-create' as const,
  ANALYTICS_UPDATE: 'analytics-update' as const,
  ANALYTICS_DELETE: 'analytics-delete' as const,

  // Brand Kit
  BRANDKIT_READ: 'brandkit-read' as const,
  BRANDKIT_CREATE: 'brandkit-create' as const,
  BRANDKIT_UPDATE: 'brandkit-update' as const,

  // Settings
  SETTINGS_READ: 'settings-read' as const,
  SETTINGS_CREATE: 'settings-create' as const,
  SETTINGS_UPDATE: 'settings-update' as const,

  // Notification
  NOTIFICATION_CONFIGURE: 'notifications-configure' as const,
} as const;

/**
 * Maps permissions to their fallback RBAC role.
 * Used when hasAccountPermissions feature flag is OFF.
 * - 'read' permissions → 'viewer' role
 * - 'create/update/delete' permissions → 'editor' role
 */
export const PERMISSION_TO_ROLE_MAP: Record<Permission, UserRole> = {
  // Account Users
  'accountusers-create': 'admin',
  'accountusers-read': 'viewer',
  'accountusers-update': 'editor',

  // Campaigns
  'campaigns-read': 'viewer',
  'campaigns-create': 'editor',
  'campaigns-update': 'editor',
  'campaigns-delete': 'editor',

  // Creatives
  'creatives-read': 'viewer',
  'creatives-create': 'editor',
  'creatives-update': 'editor',
  'creatives-delete': 'editor',

  // Audience
  'audience-read': 'viewer',
  'audience-create': 'editor',
  'audience-update': 'editor',
  'audience-delete': 'editor',

  // Data
  'data-read': 'editor',
  'data-create': 'editor',
  'data-update': 'editor',
  'data-delete': 'editor',

  // Analytics
  'analytics-read': 'viewer',
  'analytics-create': 'editor',
  'analytics-update': 'editor',
  'analytics-delete': 'editor',

  // Brand Kit
  'brandkit-read': 'editor',
  'brandkit-create': 'editor',
  'brandkit-update': 'editor',

  // Settings
  'settings-read': 'admin',
  'settings-create': 'admin',
  'settings-update': 'admin',

  // Notification
  'notifications-configure': 'admin',
};
